/**
 * Quote request modal (snippets/quote-modal.liquid).
 * Shared by layout/theme.liquid and layout/en-landing.liquid.
 */
(() => {
  const STORAGE_KEY = 'quoteItems';
  const ORIGIN_KEY = 'quoteOriginPage';
  // Set when we hand the form to Shopify's normal submit (e.g. for the spam
  // check), so we can show the thank-you view when the page reloads.
  const PENDING_KEY = 'quoteSubmitPending';

  const init = () => {
    const modal = document.querySelector('[data-quote-modal]');
    if (!modal) return;

    // Move modal to <body> so it escapes section stacking contexts
    if (!modal.dataset.movedToBody) {
      document.body.appendChild(modal);
      modal.dataset.movedToBody = 'true';
    }

    // Elements
    const dialog = modal.querySelector('.quote-modal__dialog');
    const openBtns = document.querySelectorAll('[data-quote-modal-open]');
    const closeEls = modal.querySelectorAll('[data-quote-modal-close]');
    const stepEls = modal.querySelectorAll('[data-quote-step]');
    const progressSteps = modal.querySelectorAll('[data-quote-progress-step]');
    const progressLineFill = modal.querySelector('.quote-modal__progress-line-fill');
    const addBtn = modal.querySelector('[data-quote-add]');
    const prevBtn = modal.querySelector('[data-quote-prev]');
    const continueShoppingBtn = modal.querySelector('[data-quote-continue-shopping]');
    const continueShoppingBtnStep2 = modal.querySelector('[data-quote-continue-shopping-step2]');
    const qtyInput = modal.querySelector('[data-quote-qty]');
    const detailInput = modal.querySelector('[data-quote-detail]');
    const productsListEl = modal.querySelector('[data-quote-products-list]');
    const itemsSummaryInput = modal.querySelector('[data-quote-items-summary]');
    const originInput = modal.querySelector('[data-quote-origin-page]');
    const quoteForm = document.getElementById('QuoteForm');
    const submitBtn = modal.querySelector('[data-quote-submit]');
    const errorEl = modal.querySelector('[data-quote-error]');
    const thankyouEl = modal.querySelector('[data-quote-thankyou]');

    const productTitleEl = modal.querySelector('[data-quote-product-title-el]');
    const productVariantEl = modal.querySelector('[data-quote-product-variant-el]');
    const productImageEl = modal.querySelector('[data-quote-product-image-el]');

    let lastTrigger = null;

    // Product titles and customer notes are shown via innerHTML, so escape them
    // to stop characters like & or < breaking the list.
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);

    const loadItems = () => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
      catch (e) { return []; }
    };

    const saveItems = (items) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    };

    const setOriginPage = () => {
      if (!localStorage.getItem(ORIGIN_KEY)) {
        localStorage.setItem(ORIGIN_KEY, window.location.href);
      }
      if (originInput) {
        originInput.value = localStorage.getItem(ORIGIN_KEY);
      }
    };

    const clearQuote = () => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ORIGIN_KEY);
      renderList();
    };

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    };

    const setSubmitting = (isSubmitting) => {
      if (!submitBtn) return;
      if (!submitBtn.dataset.label) submitBtn.dataset.label = submitBtn.textContent.trim();
      submitBtn.disabled = isSubmitting;
      submitBtn.setAttribute('aria-busy', String(isSubmitting));
      submitBtn.textContent = isSubmitting ? 'Sending…' : submitBtn.dataset.label;
    };

    const setStep = (step) => {
      stepEls.forEach((el) => {
        el.classList.toggle('is-active', el.dataset.quoteStep === String(step));
      });
      if (thankyouEl) {
        thankyouEl.classList.remove('is-active');
      }
      showError('');

      progressSteps.forEach((ps) => {
        const idx = Number(ps.dataset.quoteProgressStep);
        const isActive = idx === step;
        const isComplete = idx < step;

        ps.classList.toggle('is-active', isActive);
        ps.classList.toggle('is-complete', isComplete);
      });

      if (progressLineFill) {
        if (step === 1) {
          progressLineFill.style.width = '0%';
        } else if (step === 2) {
          progressLineFill.style.width = '100%';
        }
      }
    };

    const showThankyou = () => {
      stepEls.forEach(el => el.classList.remove('is-active'));
      if (progressLineFill) progressLineFill.style.width = '100%';
      if (progressSteps.length) {
        progressSteps.forEach(ps => {
          ps.classList.remove('is-active');
          ps.classList.add('is-complete');
        });
      }
      if (thankyouEl) {
        thankyouEl.classList.add('is-active');
      }
    };

    const trackSubmission = (items, originPage) => {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'quote_request',
          quote_items: items,
          quote_origin_page: originPage,
          submission_page: window.location.href
        });
      }
    };

    // Opening, closing and keyboard handling
    const getFocusable = () => Array.from(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.offsetParent !== null);

    const openModal = () => {
      modal.classList.add('is-open');
      // Wait a frame so the modal is visible before moving focus into it
      requestAnimationFrame(() => {
        const first = getFocusable().find((el) => !el.matches('[data-quote-modal-close]'));
        (first || dialog).focus();
      });
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    };

    document.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        closeModal();
        return;
      }

      // Keep Tab focus inside the modal while it is open
      if (event.key === 'Tab') {
        const focusable = getFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    const getCurrentVariant = () => {
      const variantIdInput =
        document.querySelector('form[action*="/cart/add"] [name="id"]') ||
        document.querySelector('form.product-form [name="id"]');

      const currentVariantId = variantIdInput ? variantIdInput.value : null;
      if (!currentVariantId || !window.quoteVariants) return null;

      return window.quoteVariants.find(
        (v) => String(v.id) === String(currentVariantId)
      );
    };

    const renderList = () => {
      const items = loadItems();
      productsListEl.innerHTML = '';

      if (!items.length) {
        productsListEl.innerHTML = '<p>No products in quote yet.</p>';
        return;
      }

      items.forEach((item, index) => {
        const row = document.createElement('div');
        row.className = 'quote-modal__product-row';
        row.innerHTML = `
          <div class="quote-modal__product-main">
            <div class="quote-modal__product-info">
              ${item.image ? `<img src="${escapeHtml(item.image)}" alt="">` : ''}
              <div>
                <div class="quote-modal__product-title">${escapeHtml(item.title)}</div>
                ${item.variant_title ? `<div class="quote-modal__variant">${escapeHtml(item.variant_title)}</div>` : ''}
                <div class="quote-modal__product-note">Enquiry: ${escapeHtml(item.detail) || '-'}</div>
                <button type="button" class="quote-modal__edit" data-edit-index="${index}">Edit</button>
              </div>
            </div>
            <div class="quote-modal__product-qty-row">
              <span class="quote-modal__label quote-modal__label--inline">Quantity</span>
              <div class="quote-modal__product-qty">
                <input type="number" min="1" value="${Number(item.quantity) || 1}" data-qty-index="${index}" aria-label="Quantity for ${escapeHtml(item.title)}">
                <button type="button" class="quote-modal__remove" data-remove-index="${index}" aria-label="Remove ${escapeHtml(item.title)}">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M4 7h16M10 11v6M14 11v6M9 4h6a1 1 0 0 1 .96.73L17 6H7l1.04-1.27A1 1 0 0 1 9 4zM6 7l1 12h10l1-12"
                          fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        `;
        productsListEl.appendChild(row);
      });

      productsListEl.querySelectorAll('[data-remove-index]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = Number(btn.dataset.removeIndex);
          const items = loadItems();
          items.splice(index, 1);
          saveItems(items);
          if (!items.length) clearQuote();
          else renderList();
        });
      });

      productsListEl.querySelectorAll('[data-qty-index]').forEach((input) => {
        input.addEventListener('change', () => {
          const index = Number(input.dataset.qtyIndex);
          const items = loadItems();
          items[index].quantity = Math.max(1, Number(input.value) || 1);
          input.value = items[index].quantity;
          saveItems(items);
        });
      });

      productsListEl.querySelectorAll('[data-edit-index]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = Number(btn.dataset.editIndex);
          const items = loadItems();
          const item = items[index];

          qtyInput.value = item.quantity;
          detailInput.value = item.detail;
          modal.dataset.editIndex = index;

          if (productTitleEl) productTitleEl.textContent = item.title || '';
          if (productVariantEl) productVariantEl.textContent = item.variant_title || '';
          if (productImageEl && item.image) productImageEl.src = item.image;

          setStep(1);
        });
      });
    };

    const loadActiveProductIntoStep1 = (btn) => {
      const d = btn.dataset;
      const variant = getCurrentVariant();

      let imageUrl = d.productImage || '';
      if (variant && variant.featured_image && variant.featured_image.src) {
        imageUrl = variant.featured_image.src;
      }

      if (productTitleEl) productTitleEl.textContent = d.productTitle || '';
      if (productVariantEl) productVariantEl.textContent = variant ? variant.title : '';
      if (imageUrl && productImageEl) productImageEl.src = imageUrl;

      modal.dataset.activeProductId     = d.productId;
      modal.dataset.activeProductTitle  = d.productTitle;
      modal.dataset.activeProductHandle = d.productHandle;
      modal.dataset.activeProductUrl    = d.productUrl;
      modal.dataset.activeProductImage  = imageUrl;
    };

    openBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        lastTrigger = btn;
        loadActiveProductIntoStep1(btn);
        qtyInput.value = 1;
        detailInput.value = '';
        delete modal.dataset.editIndex;
        setStep(1);
        setOriginPage();
        renderList();
        openModal();
      });
    });

    closeEls.forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    continueShoppingBtn?.addEventListener('click', closeModal);
    continueShoppingBtnStep2?.addEventListener('click', closeModal);

    prevBtn?.addEventListener('click', () => setStep(1));

    addBtn?.addEventListener('click', () => {
      const d = modal.dataset;
      const items = loadItems();
      const variant = getCurrentVariant();

      let imageUrl = d.activeProductImage || '';
      if (variant && variant.featured_image && variant.featured_image.src) {
        imageUrl = variant.featured_image.src;
      }

      const newItem = {
        id: d.activeProductId,
        title: d.activeProductTitle,
        handle: d.activeProductHandle,
        url: d.activeProductUrl,
        image: imageUrl,
        variant_id: variant ? variant.id : null,
        variant_title: variant ? variant.title : null,
        variant_sku: variant ? variant.sku : null,
        quantity: Math.max(1, Number(qtyInput.value) || 1),
        detail: detailInput.value || ''
      };

      const editIndex = modal.dataset.editIndex;
      if (editIndex !== undefined) {
        items[Number(editIndex)] = newItem;
        delete modal.dataset.editIndex;
      } else {
        items.push(newItem);
      }

      saveItems(items);
      renderList();
      setStep(2);
    });

    quoteForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      showError('');

      const items = loadItems();

      if (!items.length) {
        showError('Please add at least one product to your quote before sending it.');
        return;
      }

      if (itemsSummaryInput) {
        const summaryLines = items.map((item, idx) => {
          return [
            `Item ${idx + 1}: ${item.title}`,
            item.variant_title ? `Variant: ${item.variant_title}` : null,
            item.variant_sku ? `SKU: ${item.variant_sku}` : null,
            item.detail ? `Enquiry: ${item.detail}` : null,
            `Qty: ${item.quantity}`,
            `URL: ${item.url}`
          ].filter(Boolean).join('\n');
        });

        itemsSummaryInput.value = summaryLines.join('\n\n');
      }

      if (originInput) {
        originInput.value = localStorage.getItem(ORIGIN_KEY) || window.location.href;
      }

      const originPage = originInput ? originInput.value : '';
      setSubmitting(true);

      fetch(quoteForm.action, {
        method: 'POST',
        body: new FormData(quoteForm),
        credentials: 'same-origin'
      }).then((response) => {
        // Shopify redirects to "?contact_posted=true" only when the message was
        // actually sent. Anything else means it was not.
        if (response.ok && response.url.includes('contact_posted=true')) {
          setSubmitting(false);
          clearQuote();
          showThankyou();
          trackSubmission(items, originPage);
          return;
        }

        // Shopify wants the customer to pass its spam check. Hand over to a
        // normal form submit so they can; the quote is kept until it succeeds.
        if (response.url.includes('/challenge')) {
          localStorage.setItem(PENDING_KEY, '1');
          quoteForm.submit();
          return;
        }

        setSubmitting(false);
        showError('Sorry, we couldn’t send your request. Please check your details and try again.');
      }).catch(() => {
        setSubmitting(false);
        showError('Sorry, we couldn’t send your request. Please check your connection and try again.');
      });
    });

    // Returning from Shopify's normal form submit (after the spam check).
    if (localStorage.getItem(PENDING_KEY)) {
      localStorage.removeItem(PENDING_KEY);

      if (window.location.search.includes('contact_posted=true')) {
        const items = loadItems();
        trackSubmission(items, localStorage.getItem(ORIGIN_KEY) || '');
        clearQuote();
        showThankyou();
        openModal();
      }
    }

    if (loadItems().length && originInput) {
      originInput.value = localStorage.getItem(ORIGIN_KEY) || '';
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
